import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LabelImportantIcon from "@mui/icons-material/LabelImportantRounded";
import { CircularProgress, IconButton } from "@mui/material";
import type { GridCellParams, GridColDef, GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import { logError } from "@entrilia/shared/logging";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import useInboxStateContext from "../../../../contexts/useInboxStateContext";
import { markMessageAsRead } from "../../../../hooks/inboxState/inboxState.api";
import type { PreviewDocument } from "../../../../hooks/pdfPreview";
import { usePdfPreview } from "../../../../hooks/pdfPreview";
import { downloadAllAttachments } from "../../../../services/attachments";
import { MessageActions } from "../../../../store/state/messages/actions";
import {
  getEntityFromMessage,
  getFundsFromMessage,
  getRecipientsFromMessage,
} from "../../../../store/state/messages/messageHelper";
import { markAsImportantUpdatingSelector, messagesSelector } from "../../../../store/state/messages/selectors";
import { markAsImportant } from "../../../../store/state/messages/thunks";
import type { Message } from "../../../../store/state/messages/types";
import { MessageType } from "../../../../store/state/messages/types";
import { impersonationSelector } from "../../../../store/state/user/selectors";
import type { AppDispatch } from "../../../../store/store";
import InvestorPortalPreviewFileDialog from "../../../common/InvestorPortalPreviewFileDialog";
import MessageCategoryTag from "../../MessageCategoryTag";
import Actions from "../Actions";
import InlineItemsWithBadge from "../InlineItemsWithBadge";
import MessagePublishStatusIcon from "../MessagePublishStatusIcon";
import MessageTypeComponent from "../MessageType";
import { getColumnFlexWidth, loadColumnConfigs, saveColumnConfigs } from "./messageGridConfigHelper";

interface Props {
  selection: GridRowSelectionModel;
  setSelection: (values: GridRowSelectionModel) => void;
  navigateToMessage: (id: string) => void;
}

const MessageGrid = ({ selection, setSelection, navigateToMessage }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const { sendNotificationError } = useNotificationContext();
  const { prepareMessageForPreview } = usePdfPreview();
  const { ui, watermarkInvestorName } = useInboxStateContext();
  const messages = useSelector(messagesSelector);
  const messageUpdating = useSelector(markAsImportantUpdatingSelector);
  const isImpersonation = useSelector(impersonationSelector);
  const [previewFileData, setPreviewFileData] = useState<PreviewDocument | undefined>(undefined);

  const gridColumnConfigItems = useMemo(() => loadColumnConfigs(), []);

  const apiRef = useGridApiRef();

  const saveGridSettings = useDebounce(() => {
    if (apiRef.current) {
      saveColumnConfigs(apiRef.current);
    }
  }, 500);

  const changeMessageImportance = useCallback(
    (messageId: string) => {
      const message = messages.find((msg) => msg.id === messageId);
      if (message) {
        dispatch(markAsImportant([message.id], !message.isImportant));
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [messages, messageUpdating, dispatch]
  );

  const onDownloadAllAttachments = useCallback(
    async (message: Message) => {
      dispatch(MessageActions.UpdateDownloading(message.id, true));
      try {
        await downloadAllAttachments(message, watermarkInvestorName);
        if (!message.isRead) {
          await markMessageAsRead(message.id)(dispatch);
        }
      } catch {
        sendNotificationError("The attachments cannot be downloaded");
      } finally {
        dispatch(MessageActions.UpdateDownloading(message.id, false));
      }
    },
    [dispatch, watermarkInvestorName, sendNotificationError]
  );

  const handleColumnWidthChange = () => {
    if (!isImpersonation) {
      saveGridSettings();
    }
  };

  const showToolbar = selection.ids.size > 0;

  const columns = useMemo<GridColDef<Message>[]>(() => {
    const cols: GridColDef<Message>[] = [
      {
        field: "isImportant",
        headerName: "",
        width: 40,
        resizable: false,
        renderCell: ({ row }) => (
          <IconButton
            sx={(theme) => ({
              color: row.isImportant ? theme.palette.warning.light : theme.palette.text.secondary,
            })}
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              if (!isImpersonation) {
                changeMessageImportance(row.id);
              }
            }}
          >
            <LabelImportantIcon />
          </IconButton>
        ),
      },
      {
        field: "messageType",
        headerName: "",
        width: 60,
        minWidth: 60,
        resizable: false,
        renderCell: ({ row }) => <MessageTypeComponent message={row} />,
      },
      {
        field: "fund",
        headerName: "Fund",
        flex: getColumnFlexWidth("fund", gridColumnConfigItems) ?? 0.2,
        minWidth: 120,
        renderCell: ({ row }) => (
          <InlineItemsWithBadge
            items={getFundsFromMessage(row)}
            subItem={getEntityFromMessage(row)}
            fontWeight={row.isRead ? "normal" : "bold"}
          />
        ),
      },
      {
        field: "recipient",
        headerName: "Investor",
        flex: getColumnFlexWidth("recipient", gridColumnConfigItems) ?? 0.35,
        minWidth: 120,
        renderCell: ({ row }) => (
          <InlineItemsWithBadge items={getRecipientsFromMessage(row)} fontWeight={row.isRead ? "normal" : "bold"} />
        ),
      },
      {
        field: "subject",
        headerName: "Name",
        flex: getColumnFlexWidth("subject", gridColumnConfigItems) ?? 0.2,
        minWidth: 120,
        renderCell: ({ row }) => (
          <TypographyTooltipEllipsis
            text={row.content?.subject ?? ""}
            typographyProps={{ sx: { fontWeight: !row.isRead ? "bold" : "normal" } }}
          />
        ),
      },
      {
        field: "categoryId",
        headerName: "Category",
        flex: getColumnFlexWidth("categoryId", gridColumnConfigItems) ?? 0.15,
        minWidth: 120,
        renderCell: ({ formattedValue }: GridCellParams<Message, string>) => (
          <MessageCategoryTag categoryId={formattedValue ?? ""} />
        ),
      },
      {
        field: "sendDate",
        headerName: "Date",
        flex: getColumnFlexWidth("sendDate", gridColumnConfigItems) ?? 0.1,
        minWidth: 80,
        renderCell: ({ row }) => (
          <TypographyTooltipEllipsis
            text={convertISODateShort(row.sendDate)}
            typographyProps={{ sx: { fontWeight: !row.isRead ? "bold" : "normal" } }}
          />
        ),
      },
      {
        field: "hasAttachments",
        headerName: "",
        resizable: false,
        width: 44,
        cellClassName: "attachment",
        renderCell: ({ row }) => {
          if (row.isDownloading) {
            return <CircularProgress size={20} color="secondary" />;
          }

          if (row.hasAttachments && !row.isDownloadDisabled) {
            return (
              <IconButton
                onClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  onDownloadAllAttachments(row);
                }}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            );
          }

          return "";
        },
      },
    ];

    if (isImpersonation) {
      cols.splice(2, 0, {
        field: "messageState",
        minWidth: 100,
        headerName: "",
        resizable: false,
        renderCell: ({ row }) => {
          return (
            <>
              <MessagePublishStatusIcon publishingStatus={row.publishingStatus} />
              <TypographyTooltipEllipsis
                text={autoFormatCamelCase(row.publishingStatus)}
                typographyProps={{ sx: { fontWeight: !row.isRead ? "bold" : "normal" } }}
              />
            </>
          );
        },
      });
    }

    return cols;
  }, [changeMessageImportance, gridColumnConfigItems, isImpersonation, onDownloadAllAttachments]);

  const handleRowClick = useCallback(
    async ({ row }: GridRowParams<Message>) => {
      if (messageUpdating) {
        return;
      }

      if (row.type === MessageType.Letter) {
        navigateToMessage(row.id);
        return;
      }

      try {
        const downloadData = await prepareMessageForPreview(row, !isImpersonation, watermarkInvestorName);
        setPreviewFileData(downloadData);
      } catch (error) {
        logError(error, "[MessageGrid] prepareMessageForPreview");
        sendNotificationError("The file cannot be previewed");
      }
    },
    [
      messageUpdating,
      navigateToMessage,
      prepareMessageForPreview,
      isImpersonation,
      watermarkInvestorName,
      sendNotificationError,
    ]
  );

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      style={{ borderTopWidth: 1, borderTopStyle: "solid" }}
      actions={<Actions showSelectAll selection={selection} setSelection={setSelection} />}
    >
      <DataGrid<Message>
        sx={(theme) => ({
          ".MuiDataGrid-columnSeparator--resizable": {
            color: theme.palette.divider,
          },
          ".MuiDataGrid-columnHeaders": {
            borderTop: 1,
            borderColor: theme.palette.divider,
          },
          ".MuiDataGrid-row": {
            ".MuiDataGrid-cellCheckbox": {
              svg: {
                fontSize: "1.25rem",
              },
            },
            ".MuiDataGrid-cell": {
              outline: "none",
              borderBottomColor: theme.palette.divider,
              padding: 0,
              "&.attachment": {
                button: {
                  visibility: "hidden",
                },
              },
            },
            "&:hover, .Mui-hovered": {
              cursor: "pointer",
              ".MuiDataGrid-cell.attachment": {
                button: {
                  visibility: "visible",
                },
              },
            },
          },
        })}
        apiRef={apiRef}
        rows={messages}
        columns={columns}
        checkboxSelection
        disableColumnSorting
        loading={ui.messagesLoading || messageUpdating}
        onRowSelectionModelChange={setSelection}
        rowSelectionModel={selection}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        onColumnWidthChange={handleColumnWidthChange}
        onRowClick={handleRowClick}
      />
      {previewFileData && (
        <InvestorPortalPreviewFileDialog
          url={previewFileData.fileInfo.downloadUrl}
          fileName={previewFileData.fileInfo.fileName}
          documentViewSessionId={previewFileData.fileInfo.documentViewSessionId}
          refreshUrl={previewFileData.refresh}
          preventDownload={previewFileData.preventDownload}
          onClose={() => setPreviewFileData(undefined)}
        />
      )}
    </DataGridHeaderToolbarProvider>
  );
};

export default MessageGrid;
